// Review photo picker: resizes to ≤1600px JPEG in the browser, then uploads
// each photo straight away so submit only sends the returned ids.
import { useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { API_BASE } from '../../lib/apiBase';

const MAX = 4;

function shrink(file) {
        return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                        const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
                        const c = document.createElement('canvas');
                        c.width = Math.round(img.width * scale);
                        c.height = Math.round(img.height * scale);
                        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
                        URL.revokeObjectURL(img.src);
                        c.toBlob((b) => resolve(b || file), 'image/jpeg', 0.85);
                };
                img.onerror = () => resolve(file);
                img.src = URL.createObjectURL(file);
        });
}

export default function ReviewPhotos({ photos, setPhotos }) {
        const [busy, setBusy] = useState(false);
        const [err, setErr] = useState('');

        const add = async (e) => {
                const files = Array.from(e.target.files || []).slice(0, MAX - photos.length);
                e.target.value = '';
                if (!files.length) return;
                setBusy(true);
                setErr('');
                for (const f of files) {
                        if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(f.type) && !/\.(jpe?g|png|webp|heic)$/i.test(f.name)) {
                                setErr('Photos only (JPG, PNG or WebP).');
                                continue;
                        }
                        const blob = await shrink(f);
                        const fd = new FormData();
                        fd.append('file', blob, 'photo.jpg');
                        const r = await fetch(`${API_BASE}/api/reviews/images`, { method: 'POST', body: fd }).catch(() => null);
                        const j = r ? await r.json().catch(() => ({})) : {};
                        if (r?.ok && j.id) setPhotos((p) => [...p, { id: j.id, preview: URL.createObjectURL(blob) }]);
                        else setErr(r?.status === 413 ? 'That photo is too large (8 MB max).' : 'Could not upload a photo. Try another.');
                }
                setBusy(false);
        };

        return (
                <div data-testid="write-review-photos">
                        <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">Photos (optional, up to {MAX})</span>
                        <div className="flex flex-wrap gap-3">
                                {photos.map((p) => (
                                        <div key={p.id} data-testid="write-review-photo-thumb" className="relative h-20 w-20 border border-white/20">
                                                <img src={p.preview} alt="Your upload" className="h-full w-full object-cover" />
                                                <button type="button" aria-label="Remove photo" data-testid="write-review-photo-remove" onClick={() => setPhotos((all) => all.filter((x) => x.id !== p.id))} className="absolute -top-2 -right-2 bg-[#0A0A0A] border border-white/30 p-0.5 hover:border-[#FF9F1C]">
                                                        <X className="h-3 w-3" />
                                                </button>
                                        </div>
                                ))}
                                {photos.length < MAX && (
                                        <label className={`flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-white/25 text-white/50 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
                                                <ImagePlus className="h-5 w-5" />
                                                <span className="font-mono text-[9px] uppercase tracking-[0.14em]">{busy ? 'Uploading' : 'Add'}</span>
                                                <input data-testid="write-review-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple onChange={add} className="hidden" />
                                        </label>
                                )}
                        </div>
                        {err && <p data-testid="write-review-photo-error" className="mt-2 font-mono text-[11px] text-red-400">{err}</p>}
                </div>
        );
}
